import { Entity } from '@api';
import { PriceDisplay, SkeletonLoader } from '@design';
import { useIsAwaitingLivePrice } from '@terminal/hooks/use-live-price';
import { usePriceDisplay } from '@terminal/react-query-hooks/prices/usePriceDisplay';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './InsightsLivePrice.module.scss';

/**
 * EarningsLivePrice Component
 *
 *  Determine which single price to show based on market conditions
 * - Market Open: Shows primary price with "Market Open" label
 * - Pre-market: Shows secondary price with "Pre-Market" tag
 * - After-hours: Shows secondary price with "After-hours" tag
 * - Fallback: Shows primary price (close) with "As of last close" text
 *
 */

export interface InsightsLivePriceProps {
  entity: Entity;
  isHorizontalView?: boolean;
  showPricePercentage?: boolean;
  showDotSeparator?: boolean;
  /**
   * When true, show a skeleton until the first websocket tick arrives to prevent a
   * "flash of last close"
   */
  waitForLiveTick?: boolean;
}

export const InsightsLivePrice = ({
  entity,
  isHorizontalView,
  showPricePercentage = false,
  showDotSeparator = false,
  waitForLiveTick = false,
}: InsightsLivePriceProps) => {
  const { t } = useTranslation(['analyze']);
  const { isLoading, isValid, priceData } = usePriceDisplay(entity);

  const hasLiveTick = !!(
    priceData?.price.primary.isLive || priceData?.price.secondary?.isLive
  );
  const isAwaitingLiveTick = useIsAwaitingLivePrice({
    hasLiveTick,
    marketHours: priceData?.price.primary.marketHours,
    enabled: waitForLiveTick,
  });

  if (isLoading || isAwaitingLiveTick) {
    return (
      <div data-testid="insights-live-price-loading">
        <SkeletonLoader
          areas={[{ className: styles.priceSkeleton }]}
          withWrapper={false}
        />
      </div>
    );
  }

  if (!isValid || !priceData) {
    return null;
  }

  const { price, mappedEntity, priceFormatter } = priceData;

  const priceDisplayClassName = clsx(styles.styledPriceDisplay, {
    [styles.horizontalView]: isHorizontalView,
    [styles.hidePricePercentage]: !showPricePercentage,
    [styles.showDotSeparator]: showDotSeparator && isHorizontalView,
  });

  const getSinglePriceDisplay = () => {
    const marketHours = price.primary.marketHours;

    if (
      marketHours.isPreMarket &&
      price.secondary?.price !== undefined &&
      price.secondary?.isLive
    ) {
      return (
        <PriceDisplay
          className={priceDisplayClassName}
          price={priceFormatter(price.secondary.price)}
          currency={mappedEntity.currency}
          priceChange={price.secondary.priceChange}
          outsideHoursText={price.secondary.outsideHours}
          isPrimary={false}
          showMarketIndicator
        />
      );
    }

    if (
      marketHours.isAfterMarket &&
      price.secondary?.price !== undefined &&
      price.secondary?.isLive
    ) {
      return (
        <PriceDisplay
          className={priceDisplayClassName}
          price={priceFormatter(price.secondary.price)}
          currency={mappedEntity.currency}
          priceChange={price.secondary.priceChange}
          outsideHoursText={price.secondary.outsideHours}
          isPrimary={false}
          showMarketIndicator
        />
      );
    }

    if (marketHours.isMarketHours && price.primary?.isLive) {
      return (
        <PriceDisplay
          className={priceDisplayClassName}
          price={priceFormatter(price.primary.price ?? 0)}
          currency={mappedEntity.currency}
          priceChange={price.primary.priceChange}
          showMarketIndicator
          liveLabel={t('analyze:analysis.marketOpen')}
        />
      );
    }

    return (
      <PriceDisplay
        className={priceDisplayClassName}
        price={priceFormatter(price.primary.price ?? 0)}
        currency={mappedEntity.currency}
        priceChange={price.primary.priceChange}
        timeNode={
          <span className={styles.dateSpan}>
            {t('analyze:analysis.asOfLastClose')}
          </span>
        }
        isPrimary={false}
        showMarketIndicator
      />
    );
  };

  return <div data-testid="earnings-live-price">{getSinglePriceDisplay()}</div>;
};
